import * as Tone from 'tone';
import Instrument from './Instrument';
import SimplerVoice from './SimplerVoice';
export var PlaybackMode;
(function (PlaybackMode) {
    PlaybackMode[PlaybackMode["CLASSIC"] = 0] = "CLASSIC";
    PlaybackMode[PlaybackMode["ONESHOT"] = 1] = "ONESHOT";
})(PlaybackMode || (PlaybackMode = {}));
class Simpler extends Instrument {
    constructor(url) {
        super();
        /**
         *  Represents Simpler's Classic Mode start value in percentage.
         *  This changes the sample's start point in Classic Mode and is relative to
         *  the start flag (sampleStart)
         */
        this.startPercentage = 0;
        /**
         *  Represents Simpler's sample start flag in seconds.
         */
        this.sampleStart = 0;
        /**
         *  Represents Simpler's sample end flag in seconds.
         */
        this.sampleEnd = 0;
        /**
         *  Represents Simpler's Classic Mode length value in percentage.
         *  This changes the sample's end point in Classic Mode and is relative to
         *  the end flag (sampleEnd)
         */
        this.length = 1;
        this.loop = false;
        /**
         *  Represents Simpler's Classic Mode loop Fade value in percentage.
         *  When Loop Fade > 0, a voice will interpolate between two buffers to create the fade effect
         */
        this.loopFade = 0;
        /**
         *  Represents Simpler's Loop value in percentage.
         *  This changes which part of the sample is looped after triggering.
         *  When this value is 1, the whole sample (relative to the sampleStart, sampleEnd, start and length values)
         *  will be looped. A lower value will loop a smaller portion towards the end.
         *  Range: [0...1]
         */
        this.loopLength = 1;
        this.transpose = 0;
        this.attack = 0;
        this.decay = 0;
        this.sustain = 1;
        this.release = 0.001;
        this.fadeInTime = 0;
        this.fadeOutTime = 0;
        this.fadeLoopBuffer = null;
        this.fadeLoopBufferLoopStart = 0;
        this.fadeLoopBufferLoopEnd = 0;
        this.fadeLoopStart = 0;
        this.fadeLoopEnd = 1;
        this.activeVoices = {};
        // tslint:disable-next-line
        this._playbackMode = PlaybackMode.CLASSIC;
        this.buffer = new Tone.Buffer(url, () => {
            if (this.loop && this.loopFade > 0) {
                this.constructFadeLoopBufferIfNecessary();
            }
        });
    }
    get volume() {
        return this.output().gain.value;
    }
    set volume(volume) {
        this.output().gain.value = volume;
    }
    get start() {
        return this.startPercentage;
    }
    set start(start) {
        this.startPercentage = start;
    }
    get playbackMode() {
        switch (this._playbackMode) {
            case PlaybackMode.CLASSIC:
                return 'classic';
            case PlaybackMode.ONESHOT:
                return 'oneshot';
        }
    }
    set playbackMode(mode) {
        switch (mode) {
            case 'classic':
                this._playbackMode = PlaybackMode.CLASSIC;
                break;
            case 'oneshot':
                this._playbackMode = PlaybackMode.ONESHOT;
                break;
            default:
                throw new Error('Unknown playbackMode');
        }
    }
    calculateFadeBufferVariables(buffer, start, loop, length, fade) {
        // calculate the sample of the end flag in Live
        const endFlagSeconds = Math.min(buffer.duration, this.sampleEnd);
        const endFlagPercentage = endFlagSeconds / buffer.duration;
        const endFlagSample = Math.floor(buffer.length * endFlagPercentage);
        // calculate the sample of the start flag in Live
        const startFlagSeconds = Math.min(buffer.duration, this.sampleStart);
        const startFlagPercentage = startFlagSeconds / buffer.duration;
        const startFlagSample = Math.floor(buffer.length * startFlagPercentage);
        const bufferLength = endFlagSample - startFlagSample;
        // calculate samples for actual playback
        const startSample = Math.floor(bufferLength * start) + startFlagSeconds;
        const acceptedLength = Math.min(length, 1 - start);
        const endSample = Math.floor(bufferLength * acceptedLength) + startSample;
        const playingBufferLength = endSample - startSample;
        // calculate position of loop samples
        const loopStartSample = Math.floor(playingBufferLength * (1 - loop)) + startSample;
        const loopBufferLength = endSample - loopStartSample;
        // sometimes, the fade is higher than the amount of samples available
        // we need to clip this value to the maximum amount possible
        const requestedFadeLengthInSamples = Math.floor(loopBufferLength * fade);
        const samplesLeftBeforeStartSample = loopStartSample - startFlagSample;
        const possibleFadeLengthInSamples = Math.min(requestedFadeLengthInSamples, samplesLeftBeforeStartSample);
        return {
            startSample,
            loopStartSample,
            loopBufferLength,
            fadeLength: possibleFadeLengthInSamples
        };
    }
    constructFadeLoopBuffer(buffer, startPercentage, loop, length, fade) {
        // a fade loop consists of two 'voices' fading into each other
        // first voice starts at the normal startSample, and fades out at the end
        // the second voice starts before startSample when the first voice fades out
        // in this way, the second voice takes over from voice 1
        // however, to make it easier, when we only fade in voice 2 while voice 1 fades out,
        // we can use voice 1 afterwards because we only need the sample play at 100%.
        // if we create a buffer that normally plays at the beginning, but does the fades at the end
        // we can just loop this buffer and it sounds like the voices are taking over
        const vars = this.calculateFadeBufferVariables(buffer, startPercentage, loop, length, fade);
        const startSample = vars.startSample;
        const loopStartSample = vars.loopStartSample;
        const loopBufferLength = vars.loopBufferLength;
        const fadeLength = vars.fadeLength;
        const startBufferLength = loopStartSample - startSample;
        const bufferLength = loopBufferLength + startBufferLength;
        const fadeLoopBuffer = Tone.context.createBuffer(1, bufferLength, buffer.sampleRate);
        const fadeLoopBufferChannelData = fadeLoopBuffer.getChannelData(0);
        const bufferReadArray = buffer.getChannelData(0);
        // first we are going to render the first voice
        // this voice includes the 'start' buffer. These samples are played before looping
        for (let i = 0; i < fadeLoopBufferChannelData.length; i++) {
            const sampleIndex = i + startSample;
            let gain = 1;
            // only samples in the loop region need to have a special gain value
            if (sampleIndex >= loopStartSample) {
                gain = Math.max(1 - (i - loopStartSample) / fadeLength, 0);
                gain = Math.pow(gain, 0.5);
            }
            const sample = bufferReadArray[sampleIndex];
            fadeLoopBufferChannelData[i] = sample * gain;
        }
        // now, we are going to render the second voice
        // this voice fades in during the loop region
        const voice2StartWriteIndex = loopBufferLength - fadeLength;
        const voice2StartSample = startSample - fadeLength;
        for (let i = 0; i < fadeLoopBufferChannelData.length; i++) {
            const sampleIndex = i + startSample;
            if (sampleIndex >= loopStartSample) {
                let gain = Math.max((sampleIndex - loopStartSample) / fadeLength, 0);
                gain = Math.pow(gain, 0.5);
                // readIndex is actually a bit before the current source buffer sample index
                // this is represented by the fade-in slope in Live
                const readIndex = sampleIndex - fadeLength;
                const readSample = bufferReadArray[readIndex];
                fadeLoopBufferChannelData[i] += readSample * gain;
            }
        }
        this.fadeLoopBuffer = fadeLoopBuffer;
        this.fadeLoopBufferLoopStart = (1 - loop) * fadeLoopBuffer.duration;
        this.fadeLoopBufferLoopEnd = fadeLoopBuffer.duration;
    }
    calculateLoopPoints() {
        const sampleEnd = this.sampleEnd === null ? this.buffer.duration : this.sampleEnd;
        const sampleDuration = sampleEnd - this.sampleStart;
        const startTime = this.sampleStart + sampleDuration * this.startPercentage;
        const loopEnd = Math.min(startTime + sampleDuration * this.length, sampleDuration);
        const noFadeLoopStart = startTime + (1 - this.loopLength) * (loopEnd - startTime);
        const loopStart = noFadeLoopStart - noFadeLoopStart * this.loopFade;
        return {
            sampleEnd,
            startTime,
            loopStart,
            loopEnd
        };
    }
    constructFadeLoopBufferIfNecessary() {
        if (this.fadeLoopBuffer === null) {
            this.constructFadeLoopBuffer(this.buffer.get(), this.startPercentage, this.loopLength, this.length, this.loopFade);
        }
    }
    triggerAttack(note, time, velocity) {
        const voice = new SimplerVoice(this);
        const transpose = this.transpose + (note - 60);
        const playbackRate = Math.round(1000000 * Math.pow(2, transpose / 12)) / 1000000;
        voice.start(time, velocity, playbackRate, this._playbackMode);
        this.activeVoices[note] = voice;
    }
    triggerRelease(time, note) {
        if (Object.prototype.hasOwnProperty.call(this.activeVoices, note)) {
            this.activeVoices[note].stop(time);
        }
    }
}
export default Simpler;
//# sourceMappingURL=Simpler.js.map